import { Box, Paper } from '@mui/material';
import {
  add,
  differenceInDays,
  getDay,
  getMonth,
  getYear,
  set,
} from 'date-fns';
import React, { useEffect, useState } from 'react';
import { useQuery } from 'react-query';
import { useResizeDetector } from 'react-resize-detector';
import {
  Area,
  Bar,
  CartesianGrid,
  ComposedChart,
  Label,
  Legend,
  Line,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { useRecoilValue } from 'recoil';
import { getDayOfWeek, getMonthText, getTypeWord } from '../../common/helper';
import NoDataPage from '../commonComponent/NoDataPage';
import { SwTitleDownloadMore } from '../commonComponent/SwTitleDownloadMore';
import { SwRwwTab } from '../commonComponent/SwRwwTab';
import { RwwAverageGraphByGender } from './RwwAverageGraphByGender';
import {
  KEY_AVERAGE,
  KEY_CLASS_MONTHLY_AVERAGE_READ_RANK_LIST,
  KEY_CLASS_MONTHLY_AVERAGE_WALK_RANK_LIST,
  KEY_CLASS_MONTHLY_AVERAGE_WRITE_RANK_LIST,
  KEY_CUMULATIVE,
  KEY_READ_TEXT,
  KEY_WALK_TEXT,
  KEY_WRITE_TEXT,
} from '../../common/key';
import {
  downloadClassMonthlyDataList,
  downloadClassYearAchievementData,
  getClassMonthlyReadRankList,
  getClassMonthlyWalkRankList,
  getClassMonthlyWriteRankList,
  getClassYearReadAchievementData,
  getClassYearWalkAchievementData,
  getClassYearWriteAchievementData,
} from '../../common/api/ApiClassStat';
import { selectSchoolInfoState } from '../../common/atom';

interface Props {
  startDate: number;
  from?: string;
  isPdf?: boolean;
  isPerson?: boolean;
}

export const RwwAverageStatistics: React.FC<Props> = ({
  startDate,
  from = 'dashboard',
  isPdf = false,
  isPerson = false,
}) => {
  const schoolClassInfo = useRecoilValue(selectSchoolInfoState);
  const [reqRequest, setReqRequest] = useState<boolean>(false);
  const [requestData, setRequestData] = useState<any>();
  const [schoolSeq, setSchoolSeq] = useState<number>(0);
  const [schoolGrade, setSchoolGrade] = useState<string>('1');
  const [schoolClass, setSchoolClass] = useState<string>('1');
  const [rowCount, setRowCount] = useState<number>(1);
  const [rateData, setRateData] = useState<any>([]);
  const { width, ref } = useResizeDetector();
  const [loading, setLoading] = useState<any>(false);
  const [statType, setStatType] = useState<string>(KEY_READ_TEXT);

  const {
    data,
    isLoading,
    refetch: refetchClassMonthlyReadData,
  } = useQuery(
    KEY_CLASS_MONTHLY_AVERAGE_READ_RANK_LIST,
    () => {
      if (
        reqRequest &&
        (schoolClassInfo.targetType === 'all' ||
          (schoolClassInfo.targetType === 'person' &&
            schoolClassInfo.memberSeq > 0))
      ) {
        const newData: any = {
          schoolSeq: schoolClassInfo.schoolSeq,
          schoolGrade: schoolClassInfo.schoolGrade,
          schoolClass: schoolClassInfo.schoolClass,
          year: getYear(startDate * 1000),
          targetType: schoolClassInfo.targetType,
          memberSeq: schoolClassInfo.memberSeq,
        };
        return getClassYearReadAchievementData(newData);
      }
      return null;
    },
    {
      onSuccess: res => {
        setLoading(false);
        if (res !== null) {
          setRequestData(res);
        } else {
          setRequestData(null);
        }
      },
      onError: e => {
        setLoading(false);
        console.log(e);
      },
    }
  );

  const { refetch: refetchClassMonthlyWalkData } = useQuery(
    KEY_CLASS_MONTHLY_AVERAGE_WALK_RANK_LIST,
    () => {
      if (
        reqRequest &&
        (schoolClassInfo.targetType === 'all' ||
          (schoolClassInfo.targetType === 'person' &&
            schoolClassInfo.memberSeq > 0))
      ) {
        const newData: any = {
          schoolSeq: schoolClassInfo.schoolSeq,
          schoolGrade: schoolClassInfo.schoolGrade,
          schoolClass: schoolClassInfo.schoolClass,
          year: getYear(startDate * 1000),
          targetType: schoolClassInfo.targetType,
          memberSeq: schoolClassInfo.memberSeq,
        };
        return getClassYearWalkAchievementData(newData);
      }
      return null;
    },
    {
      onSuccess: res => {
        setLoading(false);
        if (res !== null) {
          setRequestData(res);
        } else {
          setRequestData(null);
        }
      },
      onError: e => {
        setLoading(false);
        console.log(e);
      },
    }
  );

  const { refetch: refetchClassMonthlyWriteData } = useQuery(
    KEY_CLASS_MONTHLY_AVERAGE_WRITE_RANK_LIST,
    () => {
      if (
        reqRequest &&
        (schoolClassInfo.targetType === 'all' ||
          (schoolClassInfo.targetType === 'person' &&
            schoolClassInfo.memberSeq > 0))
      ) {
        const newData: any = {
          schoolSeq: schoolClassInfo.schoolSeq,
          schoolGrade: schoolClassInfo.schoolGrade,
          schoolClass: schoolClassInfo.schoolClass,
          year: getYear(startDate * 1000),
          targetType: schoolClassInfo.targetType,
          memberSeq: schoolClassInfo.memberSeq,
        };
        return getClassYearWriteAchievementData(newData);
      }
      return null;
    },
    {
      onSuccess: res => {
        setLoading(false);
        if (res !== null) {
          setRequestData(res);
        } else {
          setRequestData(null);
        }
      },
      onError: e => {
        setLoading(false);
        console.log(e);
      },
    }
  );

  const downloadClassMonthlyData = () => {
    const newData: any = {
      type: getTypeWord(statType),
      dataType: KEY_AVERAGE,
      schoolSeq: schoolClassInfo.schoolSeq,
      schoolGrade: schoolClassInfo.schoolGrade,
      schoolClass: schoolClassInfo.schoolClass,
      year: getYear(startDate * 1000),
      month: getMonthText(startDate * 1000),
      targetType: schoolClassInfo.targetType,
      memberSeq: schoolClassInfo.memberSeq,
    };
    downloadClassYearAchievementData(newData);
  };

  useEffect(() => {
    if (reqRequest && schoolClassInfo.schoolSeq !== 0) {
      refetchClassMonthlyReadData();
    }
  }, [schoolSeq, schoolGrade, schoolClass]);

  useEffect(() => {
    if (schoolClassInfo.schoolSeq !== 0) {
      setReqRequest(true);
      refetchClassMonthlyReadData();
    }
  }, [schoolClassInfo]);

  useEffect(() => {
    if (reqRequest && schoolClassInfo.schoolSeq !== 0) {
      refetchClassMonthlyReadData();
    }
  }, [reqRequest]);

  useEffect(() => {
    if (schoolClassInfo.schoolSeq !== 0) {
      setReqRequest(true);
      refetchClassMonthlyReadData();
    }
  }, []);

  return (
    <>
      <Paper sx={{ backgroundColor: '#fff' }}>
        <SwTitleDownloadMore title='학생당 읽기/걷기/쓰기 일 평균 데이터' />
        <SwRwwTab
          onChangeTab={value => {
            setStatType(
              value === 0
                ? KEY_READ_TEXT
                : value === 1
                ? KEY_WALK_TEXT
                : KEY_WRITE_TEXT
            );
            value === 0
              ? refetchClassMonthlyReadData()
              : value === 1
              ? refetchClassMonthlyWalkData()
              : refetchClassMonthlyWriteData();
          }}
          onDownload={() => downloadClassMonthlyData()}
          showToolTip={true}
        />
        <RwwAverageGraphByGender
          stepsGoalRateAverage={[false, false]}
          startDate={startDate}
          totalAverage={requestData?.totalRate ?? 0}
          data={requestData?.rate}
          isPdf={false}
          isPerson={isPerson}
        />
      </Paper>
    </>
  );
};
